import React, { useEffect, useMemo, useState } from "react";
import { createRoot } from "react-dom/client";
import {
  Heart,
  Menu,
  Search,
  ShoppingBag,
  UserRound,
  X,
  ArrowRight,
  ShieldCheck,
  Truck,
  RotateCcw,
} from "lucide-react";
import "./styles.css";
import CheckoutModal from "./checkout";

type Product = {
  id: number;
  sku: string;
  name: string;
  slug: string;
  category?: string;
  subcategory?: string;
  material?: string;
  photo_url?: string;
  gallery?: string[];
  media?: { kind: string; url: string }[];
  variants?: {
    id: number;
    size?: string;
    color?: string;
    price?: number | string;
    stock_quantity: number | string;
  }[];
  price: number | string;
  compare_at_price?: number | string;
  available_quantity: number | string;
  short_description?: string;
  long_description?: string;
  seo_title?: string;
  seo_description?: string;
};
type Variant = NonNullable<Product["variants"]>[number];
type ProductTaxonomy = { category: string; slug: string; subcategories: { name: string; slug: string }[] };
type ContentPage = {
  slug: string;
  page_type: "institutional" | "category" | "landing";
  title: string;
  eyebrow?: string;
  summary?: string;
  content?: string;
  seo_title?: string;
  seo_description?: string;
};
type CartLine = { product: Product; variant?: Variant; quantity: number };
const lineKey = (line: Pick<CartLine, "product" | "variant">) =>
  String(line.product.id) + "-" + String(line.variant?.id || 0);
const money = (value: number | string) =>
  new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(
    Number(value || 0),
  );

const slugify = (value: string) =>
  value
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "");
const setMeta = (name: string, content: string) => {
  let node = document.querySelector(
    `meta[name='${name}']`,
  ) as HTMLMetaElement | null;
  if (!content) {
    node?.remove();
    return;
  }
  if (!node) {
    node = document.createElement("meta");
    node.name = name;
    document.head.appendChild(node);
  }
  node.content = content;
};
const applySeo = (settings: Record<string, string>) => {
  if (settings.siteTitle) document.title = settings.siteTitle;
  setMeta("description", settings.siteDescription || "");
  setMeta("keywords", settings.siteKeywords || "");
  setMeta(
    "robots",
    settings.siteNoIndex === "true" ? "noindex,nofollow" : "index,follow",
  );
  setMeta(
    "google-site-verification",
    settings.googleSearchConsoleVerification || "",
  );
  let canonical = document.querySelector(
    "link[rel='canonical']",
  ) as HTMLLinkElement | null;
  if (!canonical) {
    canonical = document.createElement("link");
    canonical.rel = "canonical";
    document.head.appendChild(canonical);
  }
  canonical.href = settings.siteCanonical || "https://rosijoias.com.br/";
};

function App() {
  const [products, setProducts] = useState<Product[]>([]),
    [loading, setLoading] = useState(true),
    [query, setQuery] = useState(""),
    [menu, setMenu] = useState(false),
    [cartOpen, setCartOpen] = useState(false),
    [cart, setCart] = useState<CartLine[]>([]),
    [checkoutOpen, setCheckoutOpen] = useState(false),
    [detail, setDetail] = useState<Product | null>(null),
    [contentPage, setContentPage] = useState<ContentPage | null>(null),
    [contentLoading, setContentLoading] = useState(false),
    [taxonomy, setTaxonomy] = useState<ProductTaxonomy[]>([]),
    [selectedVariant, setSelectedVariant] = useState<Variant | undefined>();
  useEffect(() => {
    fetch("/api/settings")
      .then((r) => (r.ok ? r.json() : Promise.reject()))
      .then(applySeo)
      .catch(() => undefined);
  }, []);
  useEffect(() => {
    const categoryMatch = window.location.pathname.match(
      /^\/categoria\/([^/]+)$/,
    );
    const plainMatch = window.location.pathname.match(/^\/([^/]+)$/);
    const slug = categoryMatch?.[1] || plainMatch?.[1];
    if (!slug || ["produto"].includes(slug)) return;
    if (slug === "minha-conta") {
      setCheckoutOpen(true);
      return;
    }
    setContentLoading(true);
    fetch("/api/commerce/store/pages/" + encodeURIComponent(slug))
      .then((response) => (response.ok ? response.json() : Promise.reject()))
      .then((page: ContentPage) => {
        setContentPage(page);
        document.title = page.seo_title || page.title + " | Rósi Dias";
        setMeta(
          "description",
          page.seo_description || page.summary || page.title,
        );
        let canonical = document.querySelector(
          "link[rel='canonical']",
        ) as HTMLLinkElement | null;
        if (!canonical) {
          canonical = document.createElement("link");
          canonical.rel = "canonical";
          document.head.appendChild(canonical);
        }
        canonical.href = location.href;
      })
      .catch(() => setContentPage(null))
      .finally(() => setContentLoading(false));
  }, []);
  useEffect(() => {
    const match = window.location.pathname.match(/^\/produto\/([^/]+)$/);
    if (!match) return;
    setLoading(true);
    fetch("/api/commerce/store/products/" + encodeURIComponent(match[1]))
      .then((response) => (response.ok ? response.json() : Promise.reject()))
      .then((product: Product) => {
        setDetail(product);
        const first = product.variants?.find(
          (variant) => Number(variant.stock_quantity) > 0,
        );
        setSelectedVariant(first);
        document.title = product.seo_title || product.name + " | Rósi Joias";
        setMeta(
          "description",
          product.seo_description || product.short_description || product.name,
        );
        let script = document.querySelector(
          "#product-jsonld",
        ) as HTMLScriptElement | null;
        if (!script) {
          script = document.createElement("script");
          script.id = "product-jsonld";
          script.type = "application/ld+json";
          document.head.appendChild(script);
        }
        script.text = JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Product",
          name: product.name,
          sku: product.sku,
          image: (product.media || [])
            .filter((media) => media.kind === "image")
            .map((media) => new URL(media.url, location.origin).href),
          description:
            product.long_description ||
            product.short_description ||
            product.name,
          offers: {
            "@type": "Offer",
            priceCurrency: "BRL",
            price: String(product.price),
            availability:
              Number(product.available_quantity) > 0
                ? "https://schema.org/InStock"
                : "https://schema.org/OutOfStock",
            url: location.href,
          },
        });
      })
      .finally(() => setLoading(false));
  }, []);
  useEffect(() => {
    fetch("/api/commerce/store/categories")
      .then((r) => (r.ok ? r.json() : Promise.reject()))
      .then((rows) => setTaxonomy(rows || []))
      .catch(() => setTaxonomy([]));
  }, []);  useEffect(() => {
    fetch("/api/commerce/store/products?limit=48")
      .then((r) => (r.ok ? r.json() : Promise.reject()))
      .then((d) => setProducts(d.items || []))
      .catch(() => setProducts([]))
      .finally(() => setLoading(false));
  }, []);
  const shown = useMemo(
    () =>
      products.filter(
        (p) =>
          !query ||
          `${p.name} ${p.category || ""} ${p.subcategory || ""}`
            .toLowerCase()
            .includes(query.toLowerCase()),
      ),
    [products, query],
  );
  const count = cart.reduce((s, l) => s + l.quantity, 0),
    total = cart.reduce((s, l) => s + Number(l.product.price) * l.quantity, 0);
  const add = (product: Product, variant?: Variant) => {
    const priced = variant?.price
      ? { ...product, price: variant.price }
      : product;
    setCart((c) => {
      const targetKey = lineKey({ product: priced, variant });
      const old = c.find((line) => lineKey(line) === targetKey);
      return old
        ? c.map((l) =>
            lineKey(l) === targetKey ? { ...l, quantity: l.quantity + 1 } : l,
          )
        : [...c, { product: priced, variant, quantity: 1 }];
    });
    setCartOpen(true);
  };
  return (
    <div>
      <div className="announcement">
        Frete especial e atendimento personalizado{" "}
        <span>Fale com uma consultora</span>
      </div>
      <header>
        <button
          className="mobile"
          onClick={() => setMenu(true)}
          aria-label="Abrir menu"
        >
          <Menu />
        </button>
        <a className="brand" href="/">
          <img
            src="/images/rosi-logo-horizontal.svg"
            alt="Rosi Dias Joias e Semijoias"
          />
        </a>
        <div className="search">
          <Search size={19} />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="O que voce esta procurando?"
          />
        </div>
        <nav className="actions">
          <button
            onClick={() => setCheckoutOpen(true)}
            aria-label="Minha conta"
          >
            <UserRound />
          </button>
          <button aria-label="Favoritos">
            <Heart />
          </button>
          <button onClick={() => setCartOpen(true)} aria-label="Sacola">
            <ShoppingBag />
            <b>{count}</b>
          </button>
        </nav>
      </header>
      <nav className="category-nav">
        {taxonomy.map((entry) => (
          <div className="category-group" key={entry.slug}>
            <a href={`/categoria/${entry.slug}`}>{entry.category}</a>
            {!!entry.subcategories.length && <div className="subcategory-menu">{entry.subcategories.map((sub) => <a key={sub.slug} href={`/categoria/${entry.slug}?subcategoria=${sub.slug}`}>{sub.name}</a>)}</div>}
          </div>
        ))}
        <a className="sale" href="/ofertas">
          Ofertas
        </a>
      </nav>
      <main>
        {detail ? (
          <section className="product-page">
            <div className="product-gallery">
              {(detail.media || []).filter((media) => media.kind === "image")
                .length ? (
                (detail.media || [])
                  .filter((media) => media.kind === "image")
                  .map((media) => (
                    <img key={media.url} src={media.url} alt={detail.name} />
                  ))
              ) : (
                <img
                  className="placeholder"
                  src="/images/rosi-mark.svg"
                  alt={detail.name}
                />
              )}
            </div>
            <div className="product-info">
              <a href="/" className="product-back">
                ← Voltar para a coleção
              </a>
              <small>
                {detail.category || detail.material || "Rósi Joias"}
              </small>
              <h1>{detail.name}</h1>
              <p>
                {detail.short_description ||
                  "Uma peça escolhida para acompanhar momentos especiais."}
              </p>
              <div className="price">
                <strong>{money(selectedVariant?.price || detail.price)}</strong>
                <span>ou em até 6x</span>
              </div>
              {detail.variants?.length ? (
                <div className="variant-picker">
                  <b>Escolha a opção</b>
                  <div>
                    {detail.variants.map((variant) => (
                      <button
                        key={variant.id}
                        className={
                          selectedVariant?.id === variant.id ? "selected" : ""
                        }
                        disabled={Number(variant.stock_quantity) <= 0}
                        onClick={() => setSelectedVariant(variant)}
                      >
                        {[variant.size, variant.color]
                          .filter(Boolean)
                          .join(" · ") || "Único"}
                        {Number(variant.stock_quantity) <= 0
                          ? " — esgotado"
                          : ""}
                      </button>
                    ))}
                  </div>
                </div>
              ) : null}
              <button
                className="product-buy"
                disabled={
                  detail.variants?.length
                    ? !selectedVariant
                    : Number(detail.available_quantity) <= 0
                }
                onClick={() => add(detail, selectedVariant)}
              >
                Adicionar à sacola
              </button>
              <div className="product-assurances">
                <span>
                  <ShieldCheck />
                  Compra segura
                </span>
                <span>
                  <Truck />
                  Envio acompanhado
                </span>
                <span>
                  <RotateCcw />
                  Troca facilitada
                </span>
              </div>
              {detail.long_description && (
                <div className="product-description">
                  <h2>Sobre a peça</h2>
                  <p>{detail.long_description}</p>
                </div>
              )}
            </div>
          </section>
        ) : contentLoading ? (
          <div className="empty">Carregando página...</div>
        ) : contentPage ? (
          <ContentPageView page={contentPage} products={products} add={add} />
        ) : (
          <>
            <section className="hero">
              <div className="hero-copy">
                <small>NOVA COLECAO</small>
                <h1>Joias que guardam historias</h1>
                <p>
                  Design delicado, brilho marcante e pecas escolhidas para
                  acompanhar todos os seus momentos.
                </p>
                <a href="#destaques">
                  Conheca a colecao <ArrowRight size={18} />
                </a>
              </div>
              <div className="hero-art">
                <img src="/images/rosi-mark.svg" alt="" />
              </div>
            </section>
            <section className="benefits">
              <div>
                <Truck />
                <span>
                  <b>Envio para todo Brasil</b>
                  <small>Acompanhe seu pedido</small>
                </span>
              </div>
              <div>
                <ShieldCheck />
                <span>
                  <b>Compra segura</b>
                  <small>Pagamento protegido</small>
                </span>
              </div>
              <div>
                <RotateCcw />
                <span>
                  <b>Troca facilitada</b>
                  <small>Atendimento humano</small>
                </span>
              </div>
            </section>
            <section className="collections">
              <article>
                <span>Aneis</span>
              </article>
              <article>
                <span>Colares</span>
              </article>
              <article>
                <span>Brincos</span>
              </article>
            </section>
            <section className="products" id="destaques">
              <div className="section-title">
                <div>
                  <small>SELECAO ROSI DIAS</small>
                  <h2>Destaques da semana</h2>
                </div>
                <a href="/colecoes">
                  Ver todos <ArrowRight size={17} />
                </a>
              </div>
              {loading ? (
                <div className="empty">Carregando selecao...</div>
              ) : shown.length ? (
                <div className="grid">
                  {shown.map((p) => (
                    <article className="card" key={p.id}>
                      <a href={`/produto/${p.slug}`} className="photo">
                        {p.photo_url ? (
                          <img src={p.photo_url} alt={p.name} />
                        ) : (
                          <img
                            className="placeholder"
                            src="/images/rosi-mark.svg"
                            alt=""
                          />
                        )}
                        {Number(p.available_quantity) <= 0 && (
                          <span className="tag">Esgotado</span>
                        )}
                        <button className="wish">
                          <Heart size={18} />
                        </button>
                      </a>
                      <div className="details">
                        <small>{p.category || p.material || "Rosi Dias"}</small>
                        <a href={`/produto/${p.slug}`}>{p.name}</a>
                        <div className="price">
                          {p.compare_at_price && (
                            <del>{money(p.compare_at_price)}</del>
                          )}
                          <strong>{money(p.price)}</strong>
                          <span>ou em ate 6x</span>
                        </div>
                        <button
                          disabled={Number(p.available_quantity) <= 0}
                          onClick={() => add(p)}
                        >
                          Adicionar a sacola
                        </button>
                      </div>
                    </article>
                  ))}
                </div>
              ) : (
                <div className="empty">
                  <img src="/images/rosi-mark.svg" />
                  <h3>A colecao esta sendo preparada</h3>
                  <p>
                    Os produtos publicados no sistema aparecerao aqui
                    automaticamente.
                  </p>
                </div>
              )}
            </section>
            <section className="reseller">
              <div>
                <small>PARA REVENDEDORAS</small>
                <h2>Leve o brilho Rosi Dias mais longe</h2>
                <p>
                  Tenha acesso ao catalogo exclusivo, precos para revenda,
                  consignacao e acompanhamento de pedidos em uma unica conta.
                </p>
                <a href="/revendedora">
                  Quero ser revendedora <ArrowRight size={18} />
                </a>
              </div>
            </section>
            <section className="newsletter">
              <img src="/images/rosi-mark.svg" />
              <div>
                <h2>Novidades escolhidas para voce</h2>
                <p>Receba lancamentos, colecoes e condicoes especiais.</p>
              </div>
              <form
                onSubmit={async (event) => {
                  event.preventDefault();
                  const form = event.currentTarget;
                  const email = String(new FormData(form).get("email") || "");
                  const response = await fetch("/api/commerce/store/leads", {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({
                      leadType: "newsletter",
                      email,
                      consent: true,
                    }),
                  });
                  if (response.ok) {
                    form.reset();
                    alert(
                      "Cadastro realizado. Você receberá nossas novidades.",
                    );
                  } else alert("Confira o e-mail informado.");
                }}
              >
                <input
                  name="email"
                  type="email"
                  required
                  placeholder="Seu melhor e-mail"
                />
                <button>Quero receber</button>
              </form>
            </section>
          </>
        )}
      </main>
      <footer>
        <div className="footer-brand">
          <img src="/images/rosi-logo-horizontal.svg" />
          <p>Joias e semijoias para celebrar a sua historia.</p>
        </div>
        <div>
          <b>Institucional</b>
          <a href="/sobre-nos">Sobre nós</a>
          <a href="/garantia">Garantia</a>
          <a href="/trocas-e-devolucoes">Trocas e devoluções</a>
        </div>
        <div>
          <b>Atendimento</b>
          <a href="/fale-conosco">Fale conosco</a>
          <a href="/minha-conta">Acompanhar pedido</a>
          <a href="/duvidas-frequentes">Dúvidas frequentes</a>
        </div>
        <div>
          <b>Sua conta</b>
          <a href="/minha-conta">Entrar</a>
          <a href="/minha-conta">Meus pedidos</a>
          <a href="/revendedora">Área da revendedora</a>
        </div>
        <small className="copyright">
          © {new Date().getFullYear()} Rosi Dias Joias e Semijoias.
        </small>
      </footer>
      {(menu || cartOpen) && (
        <div
          className="overlay"
          onClick={() => {
            setMenu(false);
            setCartOpen(false);
          }}
        />
      )}
      <aside className={`drawer left ${menu ? "open" : ""}`}>
        <button onClick={() => setMenu(false)}>
          <X />
        </button>
        <img src="/images/rosi-logo-horizontal.svg" />
        {taxonomy.map((entry) => (
          <React.Fragment key={entry.slug}>
            <a href={"/categoria/" + entry.slug}>{entry.category}</a>
            {entry.subcategories.map((sub) => <a key={sub.slug} className="drawer-subcategory" href={`/categoria/${entry.slug}?subcategoria=${sub.slug}`}>↳ {sub.name}</a>)}
          </React.Fragment>
        ))}
      </aside>
      <aside className={`drawer cart ${cartOpen ? "open" : ""}`}>
        <div className="drawer-head">
          <h2>Sua sacola ({count})</h2>
          <button onClick={() => setCartOpen(false)}>
            <X />
          </button>
        </div>
        <div className="cart-lines">
          {cart.map((l) => (
            <div className="cart-line" key={lineKey(l)}>
              <div>
                {l.product.photo_url ? (
                  <img src={l.product.photo_url} />
                ) : (
                  <img src="/images/rosi-mark.svg" />
                )}
              </div>
              <span>
                <b>{l.product.name}</b>
                {l.variant && (
                  <small>
                    {[l.variant.size, l.variant.color]
                      .filter(Boolean)
                      .join(" · ")}
                  </small>
                )}
                <small>
                  {l.quantity} × {money(l.product.price)}
                </small>
              </span>
              <button
                onClick={() =>
                  setCart((c) => c.filter((x) => lineKey(x) !== lineKey(l)))
                }
              >
                <X size={16} />
              </button>
            </div>
          ))}
          {!cart.length && (
            <div className="empty">
              <ShoppingBag />
              <p>Sua sacola esta vazia.</p>
            </div>
          )}
        </div>
        {cart.length > 0 && (
          <div className="cart-total">
            <p>
              <span>Subtotal</span>
              <b>{money(total)}</b>
            </p>
            <small>Frete calculado no checkout</small>
            <button
              onClick={() => {
                setCartOpen(false);
                setCheckoutOpen(true);
              }}
            >
              Continuar compra
            </button>
          </div>
        )}
      </aside>
      <CheckoutModal
        open={checkoutOpen}
        lines={cart}
        onClose={() => setCheckoutOpen(false)}
        onSuccess={() => setCart([])}
      />
    </div>
  );
}

function ContentPageView({
  page,
  products,
  add,
}: {
  page: ContentPage;
  products: Product[];
  add: (product: Product, variant?: Variant) => void;
}) {
  const [sending, setSending] = useState(false);
  const [message, setMessage] = useState("");
  const selectedSubcategory = new URLSearchParams(window.location.search).get("subcategoria");
  const categoryProducts = products.filter((product) => {
    if (page.slug === "ofertas") return Boolean(product.compare_at_price);
    return slugify(product.category || "") === page.slug && (!selectedSubcategory || slugify(product.subcategory || "") === selectedSubcategory);
  });

  async function submitContact(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setSending(true);
    setMessage("");
    const data = Object.fromEntries(new FormData(event.currentTarget));
    const response = await fetch("/api/commerce/store/leads", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ ...data, leadType: "contact", consent: true }),
    });
    setSending(false);
    if (response.ok) {
      event.currentTarget.reset();
      setMessage("Mensagem enviada. Nossa equipe entrará em contato.");
    } else setMessage("Não foi possível enviar. Confira os dados.");
  }

  async function submitReseller(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setSending(true);
    setMessage("");
    const data = Object.fromEntries(new FormData(event.currentTarget));
    const response = await fetch("/api/commerce/store/auth/register", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        ...data,
        customerType: "reseller",
        marketingConsent: true,
      }),
    });
    const result = await response.json().catch(() => ({}));
    setSending(false);
    if (response.ok) {
      localStorage.setItem("rosi_customer_token", result.token);
      event.currentTarget.reset();
      setMessage(
        "Cadastro enviado. Sua conta foi criada e aguarda aprovação da equipe Rósi Dias.",
      );
    } else setMessage(result.error || "Não foi possível realizar o cadastro.");
  }

  return (
    <div className="content-page">
      <section className="content-hero">
        <small>{page.eyebrow}</small>
        <h1>{page.title}</h1>
        <p>{page.summary}</p>
      </section>

      {page.page_type === "category" ? (
        <section className="products content-products">
          {categoryProducts.length ? (
            <div className="grid">
              {categoryProducts.map((product) => (
                <article className="card" key={product.id}>
                  <a href={"/produto/" + product.slug} className="photo">
                    <img
                      className={!product.photo_url ? "placeholder" : ""}
                      src={product.photo_url || "/images/rosi-mark.svg"}
                      alt={product.name}
                    />
                    {Number(product.available_quantity) <= 0 && (
                      <span className="tag">Esgotado</span>
                    )}
                  </a>
                  <div className="details">
                    <small>{[product.category, product.subcategory].filter(Boolean).join(" · ") || "Rósi Dias"}</small>
                    <a href={"/produto/" + product.slug}>{product.name}</a>
                    <div className="price">
                      {product.compare_at_price && (
                        <del>{money(product.compare_at_price)}</del>
                      )}
                      <strong>{money(product.price)}</strong>
                    </div>
                    <button
                      disabled={Number(product.available_quantity) <= 0}
                      onClick={() => add(product)}
                    >
                      Adicionar à sacola
                    </button>
                  </div>
                </article>
              ))}
            </div>
          ) : (
            <div className="empty">
              <img src="/images/rosi-mark.svg" />
              <h3>Novidades em preparação</h3>
              <p>
                Os produtos desta categoria aparecerão aqui quando forem
                publicados.
              </p>
            </div>
          )}
        </section>
      ) : (
        <section className="content-body">
          <div className="content-copy">
            {String(page.content || "")
              .split(/\n+/)
              .filter(Boolean)
              .map((paragraph, index) => (
                <p key={index}>{paragraph}</p>
              ))}
          </div>

          {page.slug === "fale-conosco" && (
            <form className="site-form" onSubmit={submitContact}>
              <h2>Envie sua mensagem</h2>
              <div className="form-grid">
                <label>
                  Nome
                  <input name="name" required />
                </label>
                <label>
                  E-mail
                  <input name="email" type="email" required />
                </label>
                <label>
                  Telefone
                  <input name="phone" required />
                </label>
                <label className="wide">
                  Como podemos ajudar?
                  <textarea name="message" required rows={5} />
                </label>
              </div>
              <button disabled={sending}>
                {sending ? "Enviando..." : "Enviar mensagem"}
              </button>
              {message && <p className="form-message">{message}</p>}
            </form>
          )}

          {page.slug === "revendedora" && (
            <form className="site-form" onSubmit={submitReseller}>
              <h2>Cadastro de revendedora</h2>
              <p>
                Crie sua conta. Após a aprovação, preços e condições de revenda
                serão liberados.
              </p>
              <div className="form-grid">
                <label>
                  Nome completo
                  <input name="name" required />
                </label>
                <label>
                  CPF ou CNPJ
                  <input name="document" required />
                </label>
                <label>
                  Telefone / WhatsApp
                  <input name="phone" required />
                </label>
                <label>
                  E-mail
                  <input name="email" type="email" required />
                </label>
                <label className="wide">
                  Senha de acesso
                  <input
                    name="password"
                    type="password"
                    minLength={10}
                    required
                    placeholder="Mínimo de 10 caracteres"
                  />
                </label>
              </div>
              <label className="consent">
                <input type="checkbox" required /> Autorizo o contato da Rósi
                Dias e confirmo os dados informados.
              </label>
              <button disabled={sending}>
                {sending ? "Criando conta..." : "Enviar cadastro"}
              </button>
              {message && <p className="form-message">{message}</p>}
            </form>
          )}
        </section>
      )}
    </div>
  );
}

createRoot(document.getElementById("root")!).render(
  <React.StrictMode>
    <App />
  </React.StrictMode>,
);
